import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import TooltipGray from "../components/TooltipGray";
import CourseDescriptionModal from "../components/CourseDescriptionModal";
import { formatDateTimeCell } from "../components/SortableTable";
import LoadingIndicator from "../components/LoadingIndicator";
import useBodyScrollLock from "../utils/useBodyScrollLock";
import PositionCreate from "./PositionCreate";
import PageTitle from "../components/PageTitle";
import { useLanguage } from "../contexts";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "http://127.0.0.1:8000"
).replace(
  /\/+$/,
  ""
);

function getStateBadgeClasses(stateCode) {
  const base =
    "inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-semibold";
  if (stateCode === "active") return `${base} bg-yellow-100 text-yellow-800 border border-yellow-200`;
  if (stateCode === "completed") return `${base} bg-green-100 text-green-800 border border-green-200`;
  if (stateCode === "pending") return `${base} bg-blue-100 text-blue-800 border border-blue-200`;
  if (stateCode === "unpublished") return `${base} bg-gray-100 dark:bg-[var(--color-bg-surface)] text-gray-700 dark:text-[var(--color-text-secondary)] border border-gray-200 dark:border-[var(--color-border)]`;
  return `${base} bg-gray-100 dark:bg-[var(--color-bg-surface)] text-gray-700 dark:text-[var(--color-text-secondary)] border border-gray-200 dark:border-[var(--color-border)]`;
}

export default function ScientificFieldSingle() {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [fieldData, setFieldData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [positionModalOpen, setPositionModalOpen] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState({
    open: false,
    title: "",
    description: "",
  });

  useBodyScrollLock(positionModalOpen);

  const isAdmin = !!(
    currentUser?.isAdmin ||
    currentUser?.role === "admin" ||
    currentUser?.is_staff ||
    currentUser?.is_superuser
  );

  useEffect(() => {
    if (!id || !currentUser) return;
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE_URL}/api/scientific-fields/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => setFieldData(res.data || null))
      .catch((err) => {
        console.error("Error fetching scientific field:", err);
        setError(t("scientificFieldSingle.loadError"));
      })
      .finally(() => setLoading(false));
  }, [id, currentUser]);

  const refreshField = async () => {
    if (!id) return;
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/scientific-fields/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFieldData(res.data || null);
    } catch (err) {
      console.error("Error refreshing scientific field:", err);
    }
  };

  const fieldName = fieldData?.name || location.state?.scientificFieldName || "";
  const rankingLink = fieldName
    ? `/ranking?scientificField=${encodeURIComponent(fieldName)}`
    : "/ranking";
  const stateCode = fieldData?.state || "";
  const stateLabel = stateCode ? t(`positionState.${stateCode}`) : "—";
  const isPositionInactive = stateCode === "unpublished";
  const isPositionLocked = stateCode === "active" || stateCode === "completed";
  const positionButtonLabel = isPositionInactive
    ? t("scientificFieldSingle.openPosition")
    : t("scientificFieldSingle.closePosition");
  const positionButtonDisabled = !fieldData || isPositionLocked;
  const positionDisabledTooltip = t("scientificFieldSingle.positionDisabledTooltip");
  const deleteButtonDisabled = !fieldData || deleting || isPositionLocked;
  const deleteDisabledTooltip = t("scientificFieldSingle.deleteDisabledTooltip");
  const editButtonDisabled = !fieldData || isPositionLocked;
  const editDisabledTooltip = t("scientificFieldSingle.editDisabledTooltip");

  const courses = useMemo(() => {
    if (!Array.isArray(fieldData?.courses)) return [];
    return fieldData.courses.map((course) => ({
      ...course,
      teachingUnits: course.teachingUnits ?? course.teaching_units ?? "",
    }));
  }, [fieldData?.courses]);

  const handleEditClick = () => {
    if (!fieldData) return;
    navigate("/scientific-fields/create#edit", {
      state: { prefillScientificField: fieldData },
    });
  };

  const handleOpenPositionClick = async () => {
    if (!fieldData) return;
    if (isPositionInactive) {
      setPositionModalOpen(true);
      return;
    }

    const confirmed = window.confirm(
      t("scientificFieldSingle.confirmClose")
    );
    if (!confirmed) return;

    const token = localStorage.getItem("token");
    if (!token || !fieldData.positionId) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/positions/${fieldData.positionId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      showToast({
        type: "success",
        message: t("scientificFieldSingle.positionClosed"),
      });
      await refreshField();
    } catch (err) {
      console.error("Error closing position:", err);
      const message = err?.response?.data?.error || t("scientificFieldSingle.positionCloseFailed");
      setError(message);
    }
  };

  const handleClosePositionModal = () => {
    setPositionModalOpen(false);
  };

  const handleDeleteClick = async () => {
    if (!id || deleting) return;
    const confirmed = window.confirm(
      t("scientificFieldSingle.confirmDelete")
    );
    if (!confirmed) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/scientific-fields/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast({
        type: "success",
        message: t("scientificFieldSingle.fieldDeleted"),
      });
      navigate("/scientific-fields/view");
    } catch (err) {
      console.error("Error deleting scientific field:", err);
      setError(t("scientificFieldSingle.fieldDeleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const positionPrefill = useMemo(() => {
    if (!fieldData) return null;
    if (isPositionInactive) {
      return {
        scientificFieldId: fieldData.id || "",
      };
    }
    return {
      id: fieldData.positionId || "",
      scientificFieldId: fieldData.id || "",
      startDate: fieldData.positionStartDate || "",
      endDate: fieldData.positionEndDate || "",
      startTime: fieldData.positionStartTime || "00:00",
      endTime: fieldData.positionEndTime || "23:59",
    };
  }, [fieldData, isPositionInactive]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-y-5 pt-5">
        <LoadingIndicator size="sm" textClassName="mt-2 text-gray-600 dark:text-[var(--color-text-secondary)]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-0 space-y-6 pb-10">
      <PageTitle>
        {fieldName ? (
          <>
            {t("scientificFieldSingle.pageTitlePrefix")} <span className="text-lg font-semibold">{fieldName}</span>
          </>
        ) : (
          t("scientificFieldSingle.pageTitle")
        )}
      </PageTitle>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}


      {positionModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm px-6 py-8"
            onClick={handleClosePositionModal}
          >
            <div
              className="relative z-[9999] w-full max-w-[700px] max-h-[86vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-[var(--color-border)] bg-white dark:bg-[var(--color-bg-card)] px-8 pt-8 pb-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={handleClosePositionModal}
                className="absolute right-6 top-5 z-10 text-3xl leading-none text-gray-600 hover:text-gray-900 dark:text-[var(--color-text-primary)]"
                aria-label={t("common.close")}
                title={t("common.close")}
              >
                &times;
              </button>

              <div
                className="
                    mx-auto w-full max-w-[620px]
                  [&_hr]:hidden
                  [&_.border-t]:border-t-0
                  [&_.border-b]:border-b-0
                  [&_form]:space-y-10
                  [&_form>div:last-child]:mt-4
                  [&_form>div:last-child]:pt-0
                  [&_form>div:last-child]:border-t-0
                  [&_button[type='submit']]:mt-0
                "
              >
                <PositionCreate
                  prefillPosition={positionPrefill}
                  inModal
                  onCancel={handleClosePositionModal}
                  onSuccess={async () => {
                    setPositionModalOpen(false);
                    await refreshField();
                    navigate(`/scientific-fields/view/${id}`);
                  }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <h2 className="text-xl font-light mb-3 text-gray-900 dark:text-[var(--color-text-primary)]">{t("scientificFieldSingle.fieldInfoHeading")}</h2>
          <div className="overflow-x-auto shadow-md rounded-lg border border-patras-capePalliser/50 dark:border-[var(--color-border)]">
            <table className="min-w-full bg-white dark:bg-[var(--color-bg-card)]">
              <thead className="bg-patras-buccaneer">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                    {t("scientificFieldSingle.colName")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                    {t("scientificFieldSingle.colSchool")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                    {t("scientificFieldSingle.colDepartment")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-patras-cameo dark:divide-[var(--color-border)]">
                <tr>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle border-r border-patras-albescentWhite dark:border-[var(--color-border)] font-semibold">
                    {fieldName || "—"}
                  </td>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                    {fieldData?.school || "—"}
                  </td>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle">
                    {fieldData?.department || "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-xl font-light text-gray-900 dark:text-[var(--color-text-primary)]">{t("scientificFieldSingle.coursesHeading")}</h2>
          </div>

          {courses.length ? (
            <div className="w-full overflow-x-auto shadow-md rounded-lg border border-patras-capePalliser/50 dark:border-[var(--color-border)]">
              <table className="min-w-full bg-white dark:bg-[var(--color-bg-card)]">
                <thead className="bg-patras-buccaneer">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colCode")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colCourseName")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colDescription")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colSemester")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colUnits")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      ECTS
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colTheory")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                      {t("scientificFieldSingle.colLab")}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                      {t("scientificFieldSingle.colCategory")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-patras-cameo dark:divide-[var(--color-border)]">
                  {courses.map((course) => (
                    <tr key={course.id || course.code}>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.code || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)] font-semibold">
                        {course.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.description ? (
                          <button
                            type="button"
                            onClick={() =>
                              setDescriptionModal({
                                open: true,
                                title: course.name ? t("scientificFieldSingle.descriptionModalTitle", { name: course.name }) : t("scientificFieldSingle.descriptionModalTitleNoName"),
                                description: course.description,
                              })
                            }
                            className="underline text-patras-buccaneer hover:text-patras-sanguineBrown dark:text-[var(--color-text-secondary)] dark:hover:text-[var(--color-primary)]"
                          >
                            {t("scientificFieldSingle.descriptionLink")}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.semester || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.teachingUnits || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.ects || "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.theory_hours ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                        {course.lab_hours ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm">
                        {course.category || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-[var(--color-text-muted)]">{t("scientificFieldSingle.noCourses")}</p>
          )}
        </div>

        <CourseDescriptionModal
          open={descriptionModal.open}
          title={descriptionModal.title}
          description={descriptionModal.description}
          onClose={() =>
            setDescriptionModal({
              open: false,
              title: "",
              description: "",
            })
          }
        />

        <div>
          <h2 className="text-xl font-light mb-3 text-gray-900 dark:text-[var(--color-text-primary)]">{t("scientificFieldSingle.positionInfoHeading")}</h2>
          <div className="overflow-x-auto shadow-md rounded-lg border border-patras-capePalliser/50 dark:border-[var(--color-border)]">
            <table className="min-w-full bg-white dark:bg-[var(--color-bg-card)]">
              <thead className="bg-patras-buccaneer">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                    {t("scientificFieldSingle.colStart")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                    {t("scientificFieldSingle.colEnd")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider border-r border-patras-albescentWhite">
                    {t("scientificFieldSingle.colStatus")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                    {t("scientificFieldSingle.colApplications")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-patras-cameo dark:divide-[var(--color-border)]">
                <tr>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle border-r border-patras-albescentWhite dark:border-[var(--color-border)] whitespace-nowrap">
                    {formatDateTimeCell(fieldData?.positionStartDate, fieldData?.positionStartTime, "00:00")}
                  </td>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle border-r border-patras-albescentWhite dark:border-[var(--color-border)] whitespace-nowrap">
                    {formatDateTimeCell(fieldData?.positionEndDate, fieldData?.positionEndTime, "23:59")}
                  </td>
                  <td className="px-6 py-4 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle border-r border-patras-albescentWhite dark:border-[var(--color-border)]">
                    <span className={getStateBadgeClasses(stateCode)}>{stateLabel}</span>
                  </td>
                  <td className="p-0 text-patras-buccaneer dark:text-[var(--color-text-primary)] text-sm text-center align-middle">
                    {fieldData?.applications !== undefined && fieldData?.applications !== null ? (
                      <Link
                        to={rankingLink}
                        className="group flex h-full w-full flex-col items-center justify-center py-4 font-semibold text-patras-buccaneer dark:text-[var(--color-text-secondary)] transition hover:bg-patras-buccaneer/10 hover:text-patras-sanguineBrown dark:hover:bg-[var(--color-bg-muted)] dark:hover:text-[var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-patras-buccaneer"
                        title={fieldName ? t("scientificFieldSingle.viewApplicationsFor", { name: fieldName }) : t("scientificFieldSingle.viewApplications")}
                        aria-label={fieldName ? t("scientificFieldSingle.viewApplicationsFor", { name: fieldName }) : t("scientificFieldSingle.viewApplications")}
                      >
                        <span className="text-base">{fieldData.applications}</span>
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {isAdmin && (
            <div className="mt-8 flex items-center justify-end gap-2">
              {isPositionLocked ? (
                <TooltipGray content={positionDisabledTooltip}>
                  <button
                    type="button"
                    onClick={handleOpenPositionClick}
                    disabled={positionButtonDisabled}
                    className="inline-flex items-center justify-center rounded-md border border-patras-buccaneer px-4 py-2 text-sm font-medium text-patras-buccaneer dark:text-white transition-colors hover:bg-patras-buccaneer hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {positionButtonLabel}
                  </button>
                </TooltipGray>
              ) : (
                <button
                  type="button"
                  onClick={handleOpenPositionClick}
                  disabled={positionButtonDisabled}
                  className="inline-flex items-center justify-center rounded-md border border-patras-buccaneer px-4 py-2 text-sm font-medium text-patras-buccaneer dark:text-white transition-colors hover:bg-patras-buccaneer hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {positionButtonLabel}
                </button>
              )}
              {isPositionLocked ? (
                <TooltipGray content={editDisabledTooltip}>
                  <button
                    type="button"
                    onClick={handleEditClick}
                    disabled={editButtonDisabled}
                    className="inline-flex items-center justify-center rounded-md bg-patras-buccaneer px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-patras-sanguineBrown disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t("scientificFieldSingle.edit")}
                  </button>
                </TooltipGray>
              ) : (
                <button
                  type="button"
                  onClick={handleEditClick}
                  disabled={editButtonDisabled}
                  className="inline-flex items-center justify-center rounded-md bg-patras-buccaneer px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-patras-sanguineBrown disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("scientificFieldSingle.edit")}
                </button>
              )}
              {isPositionLocked ? (
                <TooltipGray content={deleteDisabledTooltip}>
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    disabled={deleteButtonDisabled}
                    className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t("common.delete")}
                  </button>
                </TooltipGray>
              ) : (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={deleteButtonDisabled}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("common.delete")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
